/**
 * Shared display formatters. Use these wherever raw values are rendered to the user
 * (tables, dashboards, etc.) — NOT for input fields where the user is editing the
 * underlying number directly.
 */

/**
 * Format a number as USD currency with thousands separators and exactly 2 decimal places.
 * Returns "-" for null / undefined / NaN so empty cells render cleanly.
 *
 * Examples:
 *   formatCurrency(6135.3)    → "$6,135.30"
 *   formatCurrency(2336.2)    → "$2,336.20"
 *   formatCurrency(0)         → "$0.00"
 *   formatCurrency(null)      → "-"
 *   formatCurrency(undefined) → "-"
 */
export const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '-';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Format a date as a localized date string, parsing YYYY-MM-DD strings as LOCAL dates.
 *
 * Why this exists: `new Date("2025-04-09")` is parsed as 2025-04-09T00:00:00Z (UTC
 * midnight). When formatted in any timezone west of UTC (most US zones), it displays
 * as "4/8/2025" — one day earlier. This helper detects pure date strings and
 * constructs the Date with local-timezone year/month/day so the displayed date matches
 * the stored date.
 *
 * Full ISO timestamps (with time component) and Date objects are passed through
 * unchanged — they already carry timezone information.
 *
 * Examples:
 *   formatDate("2025-04-09")                              → "4/9/2025"
 *   formatDate("2025-04-09", { month: '2-digit', day: '2-digit', year: 'numeric' })
 *                                                          → "04/09/2025"
 *   formatDate("2025-04-09T14:30:00Z")                    → "4/9/2025" (or as TZ shifts)
 *   formatDate(null)                                       → "-"
 */
export const formatDate = (
  value: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!value) return '-';

  let date: Date;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    date = new Date(y, m - 1, d);
  } else {
    date = new Date(value);
  }

  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', options);
};

/**
 * Parse a date value as a LOCAL date (no UTC drift). Returns null for invalid input.
 *
 * For YYYY-MM-DD strings, constructs Date with local year/month/day components so
 * subsequent comparisons and .setHours() operations work on the correct calendar
 * day. For ISO timestamps with a time component, parses normally (timezone-aware).
 *
 * Use this for any date comparison/arithmetic on YYYY-MM-DD strings — not just display.
 */
export const parseLocalDate = (value: string | Date | null | undefined): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Convert a Date to a YYYY-MM-DD string using LOCAL year/month/day components.
 * Avoids the UTC drift in `date.toISOString().split('T')[0]` — that approach can
 * yield the wrong date late at night in negative-UTC-offset zones (US time), because
 * local-midnight passes before UTC-midnight does.
 *
 * Use this for date-input field values (<input type="date">) and any other place
 * where you need a local-timezone calendar date as a string.
 */
export const toLocalDateString = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
